import React from 'react';
import { graphql } from 'gatsby';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, Button } from '@material-ui/core';
import { DefaultButton } from '../components/Button/Button';
import TestimonialCard from '../components/TestimonialCard/TestimonialCard';
import AppBar from '../components/AppBar/AppBar';
import Footer from '../components/Footer/Footer';
import { H1, BodyRegular, BodySmallRegular } from '../components/Typography/Typography';
import BusinessEnquiryForm from '../components/BusinessEnquiryForm/BusinessEnquiryForm';
import SEOHelmet from '../helmet/SEOHelmet';
import { withPrismicPreview } from '@zachsz/gatsby-plugin-prismic-previews';
import HeroContainer from '../components/HeroContainer/HeroContainer';
import { useTranslation } from 'gatsby-plugin-react-i18next';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      width: '100%',
    },
    downloadButtonSecondary: {
      border: '2px solid #fff',
      borderRadius: '0px 8px 8px',
      color: '#fff',
      '&:hover,&:active': {
        border: '2px solid #fff',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
    },
    heroContent: {
      width: '100%',
      '& p': {
        marginBottom: '32px',
      },
      [theme.breakpoints.up(1200)]: {
        maxWidth: '386px',
      },
    },
    appStoreRating: {
      marginBottom: '60px',
      '& img': {
        display: 'block',
      },
    },
    awardsGroup: {
      display: 'none',
      [theme.breakpoints.up(1200)]: {
        display: 'flex',
        gap: '40px',
      },
    },
    award: {
      display: 'block',
    },
    testimonialHeader: {
      marginBottom: '48px',
    },
    testimonialContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      marginBottom: '80px',
    },
    sectionContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '48px 24px 0',
      maxWidth: '600px',
      margin: '0 auto',
      [theme.breakpoints.up(1200)]: {
        maxWidth: '1218px',
      },
    },
    navySectionContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 24px',
      maxWidth: '1218px',
      margin: '0 auto',
    },
    navyContactSectionWrapper: {
      padding: '30px 0',
      [theme.breakpoints.up(600)]: {
        padding: '130px 0',
      },
    },
    bulletPointWrapper: {
      padding: 0,
      margin: 0,
    },
    bulletPoint: {
      listStyle: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: '0 0 12px',
      padding: 0,
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    bulletPointText: {
      color: theme.palette.text.primary,
      margin: 0,
    },
    contactSectionWrapper: {
      padding: '30px 0',
      [theme.breakpoints.up(1200)]: {
        padding: '130px 0',
      },
    },
    phoneGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& img': {
        display: 'block',
        position: 'relative',
        maxWidth: '300px',
        [theme.breakpoints.up(600)]: {
          maxWidth: '500px',
          position: 'absolute',
          bottom: 0,
          right: 0,
        },
        [theme.breakpoints.up(800)]: {
          maxWidth: '600px',
          position: 'absolute',
          bottom: 0,
          right: 0,
        },
        [theme.breakpoints.up(1100)]: {
          maxWidth: '750px',
          position: 'absolute',
          bottom: 0,
          right: 0,
        },
      },
    },
    contactUsButton: {
      maxWidth: '130px',
    },
    listContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '60px',
      marginBottom: '120px',
    },
  };
});

const BusinessLandingPageV2 = ({ data, location, pageContext }) => {
  const node = data?.prismicBusinessLandingV2;
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <SEOHelmet
        title={node?.data?.meta_title.text}
        description={node?.data?.meta_description.text}
        keywords={node?.data?.meta_keywords}
        location={location}
        image={node?.data?.meta_image}
        schemaKey={node?.id}
        locale={pageContext?.language}
      />
      <AppBar
        overrideButtons={
          <Button
            className={classes.downloadButtonSecondary}
            variant="outlined"
            color="secondary"
            href="https://to.uptime.app/S5iL3GrK0cb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('businessPage.downloadCTA')}
          </Button>
        }
      />
      <main className={classes.root}>
        <HeroContainer>
          <div className={classes.heroContent}>
            <H1 color="#fff">{node?.data?.main_title.text}</H1>
            <BodyRegular color="#fff">{node?.data?.hero_description.text}</BodyRegular>
            <Box component="div" className={classes.appStoreRating}>
              <img src="/img/app-store-rating.png" alt={t('businessPage.appStoreRating.alt')} />
            </Box>
            <Box component="div" className={classes.awardsGroup}>
              <img
                className={classes.award}
                src="/img/award-apple-new.svg"
                alt={t('businessPage.awardAppleNew.alt')}
              />
              <img
                className={classes.award}
                src="/img/award-apple-best.svg"
                alt={t('businessPage.awardAppleBest.alt')}
              />
              <img className={classes.award} src="/img/award-google.svg" alt={t('businessPage.awardGoogle.alt')} />
            </Box>
          </div>
          <BusinessEnquiryForm />
        </HeroContainer>
        <div className={classes.sectionContent}>
          <H1 className={classes.testimonialHeader}>{node?.data?.testimonial_header.text}</H1>
          <div className={classes.testimonialContainer}>
            {node?.data?.testimonial.map(({ testimonial_title, body }) => (
              <TestimonialCard key={body.text} testimonialText={body.text} testimonialTitle={testimonial_title.text} />
            ))}
          </div>
          <div className={classes.listContainer}>
            {node?.data?.list?.map(({ inner_heading, inner_list }) => {
              return (
                <div>
                  <H1>{inner_heading.text}</H1>
                  <ul className={classes.bulletPointWrapper}>
                    {inner_list.raw?.map((item) => (
                      <li key={item?.text} className={classes.bulletPoint}>
                        <img src="/img/tick-icon.png" alt={t('businessEnquiryForm.tickIcon.alt')} />
                        <BodyRegular className={classes.bulletPointText}>{item?.text}</BodyRegular>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
        <Box style={{ backgroundColor: '#2B355B', position: 'relative' }}>
          <div className={classes.navySectionContent}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={5}>
                <Box className={classes.navyContactSectionWrapper}>
                  <H1 color="#fff">{node?.data?.heading1.text}</H1>
                  <BodySmallRegular color="#fff">{node?.data?.description.text}</BodySmallRegular>
                  <DefaultButton
                    onClick={() => {
                      window.scrollTo({
                        top: 100,
                        left: 100,
                        behavior: 'smooth',
                      });
                    }}
                    className={classes.contactUsButton}
                    variant="secondary"
                  >
                    {node?.data?.button_text.text}
                  </DefaultButton>
                </Box>
              </Grid>
            </Grid>
          </div>
          <Box className={classes.phoneGroup}>
            <img src="/img/phone-group.png" alt={t('businessEnquiryForm.phoneGroup.alt')} />
          </Box>
        </Box>
      </main>
      <Footer />
    </>
  );
};

export default withPrismicPreview(BusinessLandingPageV2);

export const query = graphql`
  query BusinessLandingPageV2Query($id: String, $language: String) {
    prismicBusinessLandingV2(id: { eq: $id }) {
      _previewable
      id
      prismicId
      type
      lang
      first_publication_date
      last_publication_date
      data {
        main_title {
          text
        }
        hero_description {
          text
        }
        testimonial {
          testimonial_title {
            text
          }
          body {
            text
          }
          background_color {
            text
          }
          logo {
            alt
            gatsbyImageData(width: 400)
            dimensions {
              width
            }
          }
        }
        testimonial_header {
          text
        }
        list {
          inner_heading {
            text
          }
          inner_list {
            raw
          }
        }
        heading1 {
          text
        }
        description {
          text
        }
        button_text {
          text
        }
        button_url {
          text
        }
        meta_title {
          text
        }
        meta_image {
          url
        }
        meta_description {
          text
        }
      }
    }
    locales: allLocale(filter: {language: {in: ["en-US", $language]}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
